import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Typography} from 'views/components';
import {Translate, useNavigate} from 'views/module';

import ChannelInbox from './list/ChannelInbox';
import ChannelThread from './thread/ChannelThread';
import ChannelDetail from './detail/ChannelDetail';

import backend from 'utils/backend';
import communication from 'utils/backend/server/communication/communication';
import userUtil from 'utils/miscellaneous/user/user_util';
import {readLocalStorage, writeLocalStorage} from 'utils/storage/local_storage';
import moment from 'moment';

const InboxScene = (props) => {
  const {
    selectedConversationRn,
    statusProgressWrap: progressWrap,
    user,
  } = props;
  const navigate = useNavigate();

  const channelTypes = ['SUPPORT', 'CLIENT'];

  const getOwnerRn = (conversationRn) => {
    return conversationRn.split(':').slice(0, 2).join(':');
  };

  const [channelTypeKey, setChannelTypeKey] = React.useState('SUPPORT');
  const [selectedCaregiver, setSelectedCaregiver] = React.useState(null);
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [channels, setChannels] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState();
  const [channelsStage, setChannelsStage] = React.useState('FILL');
  const [messages, setMessages] = React.useState([]);
  const [messagesStage, setMessagesStage] = React.useState('FILL');
  const [lastSeenByConversationRn, setLastSeenByConversationRn] = React.useState(() => {
    return readLocalStorage('CHANNEL_MESSAGES_LAST_SEEN') || {};
  });
  const [connectionStatus, setConnectionStatus] = React.useState(null);
  const [channelsRefreshToken, setChannelsRefreshToken] = React.useState(0);
  const [isDetailExpanded, setIsDetailExpanded] = React.useState(false);

  const channelsRequestIdRef = React.useRef(0);
  const messagesRequestIdRef = React.useRef(0);
  const wsRef = React.useRef(null);

  const isChannelsLoading = channelsStage === 'LOADING';
  const channelsLoadFailed = channelsStage === 'ERROR';

  const markConversationSeen = React.useCallback((conversationRn) => {
    setLastSeenByConversationRn((currentValues) => {
      const nextValues = {...currentValues, [conversationRn]: moment().toISOString()};
      writeLocalStorage('CHANNEL_MESSAGES_LAST_SEEN', nextValues);
      return nextValues;
    });
  }, []);

  const channelBackend = {
    load: {
      loadChannels: async (channelType, startKey, shouldReset, showProgress = true) => {
        const requestId = channelsRequestIdRef.current;

        await progressWrap(async () => {
          const output = await backend.communication.listChannelsByType(channelType, startKey);

          if (requestId !== channelsRequestIdRef.current) return;

          setChannels((currentChannels) => {
            const nextChannels = output?.list || [];
            return shouldReset ? nextChannels : currentChannels.concat(nextChannels);
          });
          setLastEvaluatedKey(output?.lastEvaluatedKey);
        }, {setStage: setChannelsStage, showProgress, debugString: 'CHANNEL_LIST_LOAD'});
      },
      loadChannelsByCaregiver: async (caregiverRn, channelType) => {
        const requestId = channelsRequestIdRef.current;

        await progressWrap(async () => {
          const output = await backend.communication.listChannelsByUser(caregiverRn, channelType);

          if (requestId !== channelsRequestIdRef.current) return;

          setChannels(output?.list || []);
          setLastEvaluatedKey(undefined);
        }, {setStage: setChannelsStage, debugString: 'CHANNEL_LIST_BY_CAREGIVER_LOAD'});
      },
      loadChannelsByClient: async (clientRn, channelType, caregiverRn) => {
        const requestId = channelsRequestIdRef.current;

        await progressWrap(async () => {
          const clientOutput = await backend.client.getClient(clientRn, false);
          const relationshipRns = Object.keys(clientOutput?.clientDetail?.relationships || {});

          const outputs = await Promise.all(relationshipRns.map((relationshipRn) => {
            return backend.communication.listChannelsByUser(relationshipRn, channelType);
          }));

          if (requestId !== channelsRequestIdRef.current) return;

          const channelByRn = new Map();
          outputs.forEach((output) => {
            (output?.list || []).forEach((channel) => {
              channelByRn.set(channel.conversationRn, channel);
            });
          });

          const clientChannels = [...channelByRn.values()].filter((channel) => {
            if (!caregiverRn) return true;

            return (channel.participants || []).some((participant) => participant.userRn === caregiverRn);
          });

          setChannels(clientChannels.sort((first, second) => {
            return (second.lastMessageAt || '').localeCompare(first.lastMessageAt || '');
          }));
          setLastEvaluatedKey(undefined);
        }, {setStage: setChannelsStage, debugString: 'CHANNEL_LIST_BY_CLIENT_LOAD'});
      },
      loadMessages: async (conversationRn) => {
        const requestId = messagesRequestIdRef.current;
        const ownerRn = getOwnerRn(conversationRn);

        await progressWrap(async () => {
          let loadedMessages = [];
          let messagesLastEvaluatedKey;

          do {
            const output = await backend.communication.listChannelMessages(
                ownerRn,
                conversationRn,
                messagesLastEvaluatedKey,
            );
            loadedMessages = loadedMessages.concat(output?.list || []);
            messagesLastEvaluatedKey = output?.lastEvaluatedKey;
          } while (messagesLastEvaluatedKey);

          if (requestId !== messagesRequestIdRef.current) return;

          setMessages(loadedMessages);
        }, {setStage: setMessagesStage, showProgress: false, debugString: 'CHANNEL_MESSAGE_LOAD'});
      },
    },
    message: {
      send: (body) => {
        if (!wsRef.current) {
          throw new Error('The channel websocket is not connected.');
        }

        return wsRef.current.send(body);
      },
    },
    connection: {
      reconnect: () => wsRef.current?.reconnect(),
    },
    channel: {
      create: async (caregiverRn) => {
        const output = await backend.communication.createChannel(caregiverRn, 'SUPPORT');
        const conversationRn = output?.conversationRn;

        if (!conversationRn) {
          throw new Error('The channel was created without a conversationRn.');
        }

        return conversationRn;
      },
    },
  };

  React.useEffect(() => {
    channelsRequestIdRef.current += 1;
    setChannels([]);
    setLastEvaluatedKey();

    if (selectedClient) {
      channelBackend.load.loadChannelsByClient(
          selectedClient.clientRn,
          channelTypeKey,
          selectedCaregiver?.caregiverRn,
      );
    } else if (selectedCaregiver) {
      channelBackend.load.loadChannelsByCaregiver(selectedCaregiver.caregiverRn, channelTypeKey);
    } else {
      channelBackend.load.loadChannels(channelTypeKey, undefined, true);
    }

    return () => {
      channelsRequestIdRef.current += 1;
    };
  }, [channelTypeKey, selectedCaregiver, selectedClient, channelsRefreshToken]);

  React.useEffect(() => {
    messagesRequestIdRef.current += 1;
    setMessages([]);

    if (!selectedConversationRn) {
      return undefined;
    }

    channelBackend.load.loadMessages(selectedConversationRn);
    markConversationSeen(selectedConversationRn);

    return () => {
      messagesRequestIdRef.current += 1;
    };
  }, [selectedConversationRn, markConversationSeen]);

  React.useEffect(() => {
    setConnectionStatus(null);

    if (!selectedConversationRn) {
      return undefined;
    }

    wsRef.current = new communication.ChannelWebsocketManager({
      onMessage: (message) => {
        setMessages((currentMessages) => currentMessages.concat(message));
        markConversationSeen(selectedConversationRn);
      },
      onMessages: (pushedMessages) => {
        setMessages(pushedMessages);
        markConversationSeen(selectedConversationRn);
      },
      onStatusChange: setConnectionStatus,
      onReconnect: () => channelBackend.load.loadMessages(selectedConversationRn),
    });
    wsRef.current.connect(selectedConversationRn);

    return () => {
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [selectedConversationRn, markConversationSeen]);

  const loadMoreChannels = () => {
    if (!lastEvaluatedKey || isChannelsLoading || selectedCaregiver || selectedClient) return;
    channelBackend.load.loadChannels(channelTypeKey, lastEvaluatedKey, false, false);
  };

  const refreshChannels = () => {
    if (isChannelsLoading) return;

    setChannelsRefreshToken((currentToken) => currentToken + 1);
  };

  const createChannel = async (caregiverRn) => {
    const conversationRn = await channelBackend.channel.create(caregiverRn);

    setChannelTypeKey('SUPPORT');
    setSelectedClient(null);
    setSelectedCaregiver(null);
    setChannelsRefreshToken((currentToken) => currentToken + 1);
    selectConversationRn(conversationRn);
  };

  const refreshMessages = () => {
    if (!selectedConversationRn || messagesStage === 'LOADING') return;

    messagesRequestIdRef.current += 1;
    channelBackend.load.loadMessages(selectedConversationRn);

    if (connectionStatus === communication.CHANNEL_CONNECTION_STATUS.DISCONNECTED) {
      channelBackend.connection.reconnect();
    }
  };

  const selectChannelTypeKey = (nextChannelTypeKey) => {
    setChannelTypeKey(nextChannelTypeKey);

    if (nextChannelTypeKey !== 'CLIENT') {
      setSelectedClient(null);
    }
  };

  const selectConversationRn = (conversationRn) => {
    if (conversationRn) {
      navigate(`/inbox/${encodeURIComponent(conversationRn)}`);
      return;
    }
    navigate('/inbox');
  };

  const selectedChannel = channels.find((channel) => channel.conversationRn === selectedConversationRn);

  const getChannelTitle = React.useCallback((channel) => {
    const names = (channel.participants || [])
        .filter((participant) => participant.userRn !== user?.userRn)
        .map((participant) => userUtil.getFullName(participant.firstName, participant.lastName).trim())
        .filter(Boolean);

    return names.length ? names.join(', ') : getOwnerRn(channel.conversationRn);
  }, [user]);

  const isUnread = React.useCallback((channel) => {
    const latestMessage = channel.latestMessage;
    if (!latestMessage || latestMessage.requesterRn === user?.userRn) {
      return false;
    }

    const lastSeenTime = lastSeenByConversationRn[channel.conversationRn];
    return !lastSeenTime || moment(latestMessage.createdAt).isAfter(moment(lastSeenTime));
  }, [lastSeenByConversationRn, user]);

  const unreadCount = React.useMemo(() => {
    return channels.filter(isUnread).length;
  }, [channels, isUnread]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: {xs: 'column', md: 'row'},
        width: '100%',
        height: {md: '97vh'},
        minHeight: {md: '97vh'},
        overflow: {md: 'hidden'},
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #ECECF1',
      }}
    >
      <ChannelInbox
        channels={channels}
        channelTypeKey={channelTypeKey}
        channelTypes={channelTypes}
        setChannelTypeKey={selectChannelTypeKey}
        isLoading={isChannelsLoading}
        loadFailed={channelsLoadFailed}
        getChannelTitle={getChannelTitle}
        isUnread={isUnread}
        lastEvaluatedKey={lastEvaluatedKey}
        loadMoreChannels={loadMoreChannels}
        refreshChannels={refreshChannels}
        createChannel={createChannel}
        selectedCaregiver={selectedCaregiver}
        setSelectedCaregiver={setSelectedCaregiver}
        selectedClient={selectedClient}
        setSelectedClient={setSelectedClient}
        selectedConversationRn={selectedConversationRn}
        selectConversationRn={selectConversationRn}
        unreadCount={unreadCount}
        user={user}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: {xs: '480px', md: 0},
        }}
      >
        {
          selectedConversationRn ?
            <ChannelThread
              conversationRn={selectedConversationRn}
              channelBackend={channelBackend}
              channelTitle={selectedChannel ? getChannelTitle(selectedChannel) : getOwnerRn(selectedConversationRn)}
              messages={messages}
              isLoading={messagesStage === 'LOADING'}
              loadFailed={messagesStage === 'ERROR'}
              connectionStatus={connectionStatus}
              refreshMessages={refreshMessages}
            /> :
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#6D7280',
                px: {xs: 2, md: 2, lg: 3},
                py: 3,
              }}
            >
              <Translate expression='inbox.List.NoChannelSelected' />
            </Typography>
        }
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          width: {xs: '100%', md: isDetailExpanded ? '364px' : '52px'},
          borderLeft: {md: '1px solid #ECECF1'},
          borderTop: {xs: '1px solid #ECECF1', md: 'none'},
          minHeight: {xs: isDetailExpanded ? '320px' : 0, md: 0},
          overflowY: {md: 'auto'},
          transition: 'width 150ms ease',
        }}
      >
        <ChannelDetail
          channel={selectedChannel}
          isExpanded={isDetailExpanded}
          toggleExpanded={() => setIsDetailExpanded((currentValue) => !currentValue)}
        />
      </Box>
    </Box>
  );
};

InboxScene.propTypes = {
  selectedConversationRn: PropTypes.string,
  statusProgressWrap: PropTypes.func.isRequired,
  user: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(InboxScene));
